<template>
	<button :data-size="size" :data-text="!!text" :title="title || text" :aria-label="ariaLabel || title || text"
			class="floated-menu-button" @click="onClick">
		<template v-if="badge || badgeIndicator">
			<div class="badge" :aria-label="ariaBadgeLabel">
				<template v-if="badge != null && badge !== ''">{{ badge }}</template>
			</div>
		</template>
		<slot></slot>
		<template v-if="text">
			<span class="text">{{ text }}</span>
		</template>
		<template v-if="shortcut">
			<keyboard-shortcut :control="controlShortcut" :class="{ 'shortcut-inline': shortcutInline }"
							   :label-shortcut="shortcut">
			</keyboard-shortcut>
		</template>
	</button>
</template>

<script lang="ts" setup>
	import KeyboardShortcut from "@shared-components/accessibility/keyboard-shortcut.vue";
	import useTts from "@shared-composables/accessibility/useTts";
	import { inject } from "vue";
	import { hapticInjectionKey } from "@shared-utils/accessibility/haptic";

	const props = withDefaults(defineProps<{
		text?: string,
		title?: string,
		ariaLabel?: string,
		badge?: number | string | null,
		badgeIndicator?: boolean,
		ariaBadgeLabel?: string,
		size?: "sm" | "md",
		shortcut?: string,
		controlShortcut?: boolean,
		shortcutInline?: boolean,
		ttsLabel?: string,
	}>(), {
		size: "md"
	});

	const { isTtsEnabled, speak } = useTts();

	// Haptic au click (leaf). Web/admin ne fournissent pas le hook → no-op. Dédup par geste côté provider.
	const triggerHaptic = inject(hapticInjectionKey, () =>
	{
	});

	const onClick = () =>
	{
		void triggerHaptic();
		const label = props.ttsLabel || props.title || props.text;
		if (isTtsEnabled.value && label)
		{
			speak(label);
		}
	};
</script>

<style scoped>
	@reference "@main-css";

	.floated-menu-button {
		@apply aspect-square shrink-0 grow-0 relative;
		@apply shadow-none transition-colors cursor-pointer rounded-full;
		@apply flex justify-center items-center;
		@apply bg-vert-sylvestre dark:bg-vert-anthracite;
		@apply focus-visible:element-kbd-focus;
		@apply accessibility-hover-on-focus;
		@apply w-[48px] h-[48px];
		@apply z-30;

		@variant coach {
			@apply bg-bleu-de-prusse;

			@variant dark {
				@apply bg-bleu-ceruleen;
			}
		}

		@variant app-color-rouge {
			background-color: color-mix(in srgb, var(--color-rouge-auburn), black 30%);

			@variant dark {
				background-color: color-mix(in srgb, var(--color-rouge-auburn-alt), black 65%);
			}
		}

		@variant app-color-orange {
			background-color: color-mix(in srgb, var(--color-orange-mandarine), black 30%);

			@variant dark {
				background-color: color-mix(in srgb, var(--color-orange-mandarine-alt), black 65%);
			}
		}

		@variant app-color-jaune {
			background-color: color-mix(in srgb, var(--color-jaune-jasmine), black 30%);

			@variant dark {
				background-color: color-mix(in srgb, var(--color-jaune-jasmine-alt), black 65%);
			}
		}

		@variant app-color-bleu {
			background-color: color-mix(in srgb, var(--color-bleu-ceruleen), black 30%);

			@variant dark {
				background-color: color-mix(in srgb, var(--color-bleu-ceruleen-alt), black 65%);
			}
		}

		@variant app-color-violet {
			background-color: color-mix(in srgb, var(--color-violet-prune), black 30%);

			@variant dark {
				background-color: color-mix(in srgb, var(--color-violet-prune-alt), black 65%);
			}
		}

		@variant app-color-magenta {
			background-color: color-mix(in srgb, var(--color-magenta-brume), black 30%);

			@variant dark {
				background-color: color-mix(in srgb, var(--color-magenta-brume-alt), black 65%);
			}
		}

		@variant app-color-marron {
			background-color: color-mix(in srgb, var(--color-marron-sepia), black 30%);

			@variant dark {
				background-color: color-mix(in srgb, var(--color-marron-sepia-alt), black 65%);
			}
		}

		@variant app-color-gris {
			background-color: color-mix(in srgb, var(--color-gris-perle), black 30%);

			@variant dark {
				background-color: color-mix(in srgb, var(--color-gris-perle-alt), black 65%);
			}
		}

		&:deep([data-icon]) {
			@apply text-white transition-all;
			@apply duration-300;
			@apply w-[25px] h-[25px];
		}

		@media (hover: hover) {
			&:hover {
				@apply bg-black/50 rounded-full;

				@variant coach {
					background-color: color-mix(in srgb, var(--color-bleu-de-prusse), black 40%);

					@variant dark {
						background-color: color-mix(in srgb, var(--color-bleu-ceruleen), black 40%);
					}
				}

				@variant app-color-rouge {
					background-color: color-mix(in srgb, var(--color-rouge-auburn), black 50%);

					@variant dark {
						background-color: color-mix(in srgb, var(--color-rouge-auburn-alt), black 80%);
					}
				}

				@variant app-color-orange {
					background-color: color-mix(in srgb, var(--color-orange-mandarine), black 50%);

					@variant dark {
						background-color: color-mix(in srgb, var(--color-orange-mandarine-alt), black 80%);
					}
				}

				@variant app-color-jaune {
					background-color: color-mix(in srgb, var(--color-jaune-jasmine), black 50%);

					@variant dark {
						background-color: color-mix(in srgb, var(--color-jaune-jasmine-alt), black 80%);
					}
				}

				@variant app-color-bleu {
					background-color: color-mix(in srgb, var(--color-bleu-ceruleen), black 50%);

					@variant dark {
						background-color: color-mix(in srgb, var(--color-bleu-ceruleen-alt), black 80%);
					}
				}

				@variant app-color-violet {
					background-color: color-mix(in srgb, var(--color-violet-prune), black 50%);

					@variant dark {
						background-color: color-mix(in srgb, var(--color-violet-prune-alt), black 80%);
					}
				}

				@variant app-color-magenta {
					background-color: color-mix(in srgb, var(--color-magenta-brume), black 50%);

					@variant dark {
						background-color: color-mix(in srgb, var(--color-magenta-brume-alt), black 80%);
					}
				}

				@variant app-color-marron {
					background-color: color-mix(in srgb, var(--color-marron-sepia), black 50%);

					@variant dark {
						background-color: color-mix(in srgb, var(--color-marron-sepia-alt), black 80%);
					}
				}

				@variant app-color-gris {
					background-color: color-mix(in srgb, var(--color-gris-perle), black 50%);

					@variant dark {
						background-color: color-mix(in srgb, var(--color-gris-perle-alt), black 80%);
					}
				}

				&:deep(> [data-icon]) {
					@apply -rotate-45 scale-125;
				}
			}
		}

		&:deep(.badge) {
			@apply z-30 badge-pill absolute top-[-3px] right-[-3px];
		}

		@variant accessibility-font-size-150 {
			@apply md:w-[52px] md:h-[52px];

			&:deep([data-icon]) {
				@apply md:w-[28px] md:h-[28px];
			}

			@variant accessibility-font-size-200 {
				@apply md:w-[56px] md:h-[56px];

				&:deep([data-icon]) {
					@apply md:w-[30px] md:h-[30px];
				}
			}
		}

		@variant tablet {
			@apply w-[52px] h-[52px];

			&:deep([data-icon]) {
				@apply w-[28px] h-[28px];
			}

			@variant accessibility-font-size-150 {
				@apply w-[54px] h-[54px];

				&:deep([data-icon]) {
					@apply w-[29px] h-[29px];
				}

				@variant accessibility-font-size-200 {
					@apply w-[56px] h-[56px];

					&:deep([data-icon]) {
						@apply w-[30px] h-[30px];
					}
				}
			}
		}

		&:has(.text) {
			@apply md:w-auto md:aspect-auto md:px-2 md:gap-2;
		}

		.text {
			@apply hidden text-sm md:block tablet:block;
		}

		&[data-size="sm"] {
			@apply h-8 py-0.5;

			.text {
				@apply text-xs;
			}
		}

		&:has(.keyboard-shortcut.shortcut-inline) {
			@apply w-auto aspect-auto px-2 gap-1;
		}
	}
</style>
